import { Inject, Injectable, makeStateKey, Optional, TransferState } from '@angular/core';
import {
  throwError as observableThrowError,
  of,
  Observable,
} from 'rxjs';
import { map, catchError, timeout, tap, switchMap } from 'rxjs/operators';
import { ApiResponse, ApiError, RawApiResponse } from './api-response';
import * as P from 'pino';
import { LogginService } from './logging/logging-provider';
import { SsrService } from './ssr.service';
import { SmartHttpService } from './smart-http.service';
import { CachePreference, UniversalCacheService } from './caching';
import { EnvService } from './env.service';
import { HTTP_TRANSFER_CACHE_ORIGIN_MAP, HttpParams } from '@angular/common/http';
import { LocalDataStorageService } from './local-data-storage.service';
import { generateHash } from './utils/hash';

@Injectable({
  providedIn: 'root',
})
export class CachingHttpService {
  SERVER_TIMEOUT = 5000;
  BROWSER_TIMEOUT = 60000;

  // counter = 0;

  constructor(
    private smartHttp: SmartHttpService,
    private cacheService: UniversalCacheService,
    private transferState: TransferState,
    private envService: EnvService,
    private storage: LocalDataStorageService,
    private loggingService: LogginService,
    private ssrService: SsrService,
    @Optional() @Inject(HTTP_TRANSFER_CACHE_ORIGIN_MAP) private transferStateOriginMap
  ) {
    this._logger = this.loggingService.createLogger('caching-http-service');
  }

  private _logger: P.Logger;
  //TODO add auth headers and localization params (language)

  get logger() {
    return this._logger;
  }

  public get<TData, TError = {}>(
    url: string,
    params: { [index: string]: any },
    ttl?: number,
    cachePreference: CachePreference = CachePreference.ClientAndServer
  ): Observable<ApiResponse<TData, TError>> {

    const serverCachingEnabled = this.envService.cacheConfig.isServerCacheEnabled && (cachePreference == CachePreference.ServerOnly
      || cachePreference == CachePreference.ClientAndServer);

    const clientCachingEnabled = this.envService.cacheConfig.isClientCacheEnabled && (cachePreference == CachePreference.ClientOnly
      || cachePreference == CachePreference.ClientAndServer);

    const cachingEnabled = this.ssrService.isPlatformBrowser() && clientCachingEnabled
      || this.ssrService.isPlatformServer() && serverCachingEnabled;

    if (cachingEnabled) {


      // console.info('Caching enabled for request: ', url);
      let language = '';
      if (this.storage.isUiLanguageSet()) {
        language = this.storage.getUiCulture();
      }

      const key = this.makeCacheKey(url, params, 'GET', language, 'json', this.envService.appVersion);
      this.logger.trace('Cache key for request: %s is %s', url, key);
      const stateKey = makeStateKey<ApiResponse<TData, TError>>(`cache:${key}`);

      const result = this.cacheService.has(key).pipe(switchMap(hasKey => {

        if (hasKey) {
          this.logger.trace('Cache hit for request: %s', url);

          let data = this.cacheService.get<ApiResponse<TData, TError>>(key);

          //fill transfer state only when running on server
          if (this.ssrService.isPlatformServer() && !this.transferState.hasKey(stateKey)) {
            data = data.pipe(tap(d => {
              this.logger.trace('Filling transfer state for request: %s', url);
              this.transferState.set(stateKey, d);
            }));
          }

          return data.pipe(map(c => this.safeCopy(c)));
        }
        else if (this.ssrService.isPlatformBrowser() && this.transferState.hasKey(stateKey)) {
          //when running on browser if transfer state has the data, we can use it instead of making a request
          //by the way filling cache with transfer state data
          const data = this.transferState.get(stateKey, null);
          return this.cacheService.set(key, data, ttl || this.envService.cacheConfig.defaultTtl).pipe(map(_ => {
            return this.safeCopy(data);
          }));
        }
        else {
          //if cas
          this.logger.info('Cache miss for request: %s', url);
          return this.smartHttp.get<TData, TError>(url, params, false, false).pipe(switchMap((response: ApiResponse<TData, TError>) => {
            this.logger.debug('Fill cache for request: %s', url);
            this.logger.debug('effective ttl: %d', ttl || this.envService.cacheConfig.defaultTtl);

            return this.cacheService.set(key, response, ttl || this.envService.cacheConfig.defaultTtl).pipe(map(_ => {
              if (this.ssrService.isPlatformServer()) {
                this.transferState.set(stateKey, response);
              }
              return this.safeCopy(response);
            }));
            //after cache miss fill transfer state with the response only if running on server          
          }));
        }

      }));

      return result;
    }
    else {
      return this.smartHttp.get<TData, TError>(url, params);
    }

  }

  safeCopy<TData>(data: TData): TData {

    if (data === null || data === undefined) {
      return data;
    }

    if(this.ssrService.isPlatformServer()){
      return structuredClone(data);
    }
    else{
      return JSON.parse(JSON.stringify(data));
    }
  }

  makeCacheKey(url: string, params: { [index: string]: string }, method: string, language, responseType: string, appVersion: string
  ): string {
    // make the params encoded same as a url so it's easy to identify
    //const { params, method, responseType } = request;
    let encodedParams = this.sortAndConcatParams(params);

    if (encodedParams.length == 0 && encodedParams == '') {
      encodedParams = '-';
    }

    // let serializedBody = request.serializeBody();
    // if (serializedBody instanceof URLSearchParams) {
    //   serializedBody = sortAndConcatParams(serializedBody);
    // } else if (typeof serializedBody !== 'string') {
    //   serializedBody = '';
    // }

    let normalizedUrl = url;
    if (this.transferStateOriginMap !== null && this.ssrService.isPlatformServer()) {
      const origin = URL.parse(url).origin;
      const mappedOrigin = this.transferStateOriginMap[origin];

      if (mappedOrigin !== null && mappedOrigin !== undefined) {
        //console.log('Origin: %s, Mapped origin: %s', origin, mappedOrigin);
        normalizedUrl = url.replace(origin, mappedOrigin);
      }
    }

    const key = ['http-client', method, responseType, normalizedUrl, language, encodedParams, appVersion].join('|');
    //console.log('Cache key: ', key);
    const hash = generateHash(key);

    return hash;
  }




  sortAndConcatParams(params: { [index: string]: string | number | [] }): string {
    let p = new HttpParams();

    for (const pp in params) {
      if (Object.prototype.hasOwnProperty.call(params, pp) && params[pp]) {
        if (params[pp] instanceof Array) {
          params[pp].forEach((item) => {
            p = p.append(pp, item);
          });
        } else {
          p = p.append(pp, params[pp]);
        }
      }
    }


    return [...p.keys()]
      .sort()
      .map((k) => `${k}=${p.getAll(k)}`)
      .join('&');
  }

  // generateHash(value: string): string {
  //   let hash = 0;

  //   for (const char of value) {
  //     hash = (Math.imul(31, hash) + char.charCodeAt(0)) << 0;
  //   }

  //   // Force positive number hash.
  //   // 2147483647 = equivalent of Integer.MAX_VALUE.
  //   hash += 2147483647 + 1;

  //   return hash.toString();
  // }
}
